import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import { TreeItemValue } from '@fluentui/react-components';

import { ITreeIndexes, ITreeItem } from './types';

interface SelectionTreeContextProps {
  items: ITreeItem[];
  isLoading: boolean;
  searchQuery: string;
  itemsMap: Map<TreeItemValue, ITreeIndexes>;
  openItems: Set<TreeItemValue>;
  updateItems: (newItems: ITreeItem[]) => void;
  updateSearchQuery: (query: string) => void;
  updateItemsMap: (newItemsMap: Map<TreeItemValue, ITreeIndexes>) => void;
  updateOpenItems: (newOpenItems: Set<TreeItemValue>) => void;
  updateIsLoading: (loading: boolean) => void;
}

export const SelectionTreeContext = createContext<SelectionTreeContextProps>(null);
export const useSelectionTreeContext = () => useContext(SelectionTreeContext);

interface SelectionTreeProviderProps {
  children: React.ReactNode;
}

export function SelectionTreeProvider({
  children,
}: SelectionTreeProviderProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [openItems, setOpenItems] = useState(new Set<TreeItemValue>());
  const [searchQuery, setSearchQuery] = useState('');
  const [itemsMap, setItemsMap] = useState(new Map());

  const updateItems = useCallback((newItems) => {
    setItems(newItems);
  }, []);

  const updateSearchQuery = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const updateItemsMap = useCallback((newItemsMap) => {
    setItemsMap(newItemsMap);
  }, []);

  const updateOpenItems = useCallback((newOpenItems) => {
    setOpenItems(newOpenItems);
  }, []);

  const updateIsLoading = useCallback((loading) => {
    setIsLoading(loading);
  }, []);

  const context = useMemo(() => ({
    items,
    openItems,
    itemsMap,
    searchQuery,
    isLoading,
    updateItems,
    updateOpenItems,
    updateSearchQuery,
    updateItemsMap,
    updateIsLoading,
  }), [
    items,
    openItems,
    itemsMap,
    searchQuery,
    isLoading,
    updateItems,
    updateOpenItems,
    updateSearchQuery,
    updateItemsMap,
    updateIsLoading,
  ]);

  return (
    <SelectionTreeContext.Provider value={context}>
      {children}
    </SelectionTreeContext.Provider>
  );
}
